/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/splendor.tsx
   All Splendor domain cards with text pre-rendered as JSX.
   Edit `rendered` to update display. Edit `text` to update search.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  HOPE,
  STRESS,
  SPELLCAST_ROLL,
  VERY_CLOSE,
  CLOSE,
  FAR,
  MELEE,
  VULNERABLE,
  LOADOUT,
  VAULT,
  HIT_POINTS,
  HIT_POINT,
  ARMOR_SLOT,
  LONG_REST,
} from '../../templates';

const splendorCards: DomainCard[] = [
  {
    name: "Bolt Beacon",
    level: 1,
    domain: "Splendor",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll** against a target within Far range. On a success, **spend a Hope** to send a bolt of shimmering light toward them, dealing **d8+2** magic damage using your Proficiency. The target becomes temporarily Vulnerable and glows brightly until this condition is cleared.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {FAR} range. On a success, <strong className="inline-strong">spend a {HOPE}</strong> to send a bolt of shimmering light toward them, dealing <strong className="inline-strong">d8+2</strong> magic damage using your Proficiency. The target becomes temporarily {VULNERABLE} and glows brightly until this condition is cleared.</p>
      </>
    ),
  },
  {
    name: "Mending Touch",
    level: 1,
    domain: "Splendor",
    type: "Spell",
    recallCost: 1,
    text: "You lay your hands upon a creature and channel healing magic to close their wounds. When you can take a few minutes to focus on the target you're helping, you can **spend 2 Hope** to clear a Hit Point or a Stress on them.\n\nOnce per long rest, when you spend this healing time learning something new about them or revealing something about yourself, you can clear 2 Hit Points or 2 Stress on them instead.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You lay your hands upon a creature and channel healing magic to close their wounds. When you can take a few minutes to focus on the target you're helping, you can <strong className="inline-strong">spend 2 {HOPE}</strong> to clear a {HIT_POINT} or a {STRESS} on them.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Once per {LONG_REST}, when you spend this healing time learning something new about them or revealing something about yourself, you can clear 2 {HIT_POINTS} or 2 {STRESS} on them instead.</p>
      </>
    ),
  },
  {
    name: "Reassurance",
    level: 1,
    domain: "Splendor",
    type: "Ability",
    recallCost: 0,
    text: "Once per rest, after an ally attempts an action roll but before the consequences take place, you can offer assistance or words of support. When you do, your ally can reroll their dice.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, after an ally attempts an action roll but before the consequences take place, you can offer assistance or words of support. When you do, your ally can reroll their dice.</p>
      </>
    ),
  },
  {
    name: "Final Words",
    level: 2,
    domain: "Splendor",
    type: "Spell",
    recallCost: 1,
    text: "You can infuse a corpse with a moment of life to speak with it. Make a **Spellcast Roll (13)**. On a success with Hope, the corpse answers up to three questions. On a success with Fear, the corpse answers one question. The corpse answers truthfully, but it can't impart information it didn't know in life. On a failure, or once the corpse has finished answering your questions, the body turns to dust.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You can infuse a corpse with a moment of life to speak with it. Make a {SPELLCAST_ROLL} (13). On a success with {HOPE}, the corpse answers up to three questions. On a success with Fear, the corpse answers one question. The corpse answers truthfully, but it can't impart information it didn't know in life. On a failure, or once the corpse has finished answering your questions, the body turns to dust.</p>
      </>
    ),
  },
  {
    name: "Healing Hands",
    level: 2,
    domain: "Splendor",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll (13)** and target a creature other than yourself within Melee range. On a success, **mark a Stress** to clear 2 Hit Points or 2 Stress on the target. On a failure, **mark a Stress** to clear a Hit Point or a Stress on the target. You can't heal the same target again until your next long rest.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (13) and target a creature other than yourself within {MELEE} range. On a success, <strong className="inline-strong">mark a {STRESS}</strong> to clear 2 {HIT_POINTS} or 2 {STRESS} on the target. On a failure, <strong className="inline-strong">mark a {STRESS}</strong> to clear a {HIT_POINT} or a {STRESS} on the target. You can't heal the same target again until your next {LONG_REST}.</p>
      </>
    ),
  },
  {
    name: "Second Wind",
    level: 3,
    domain: "Splendor",
    type: "Ability",
    recallCost: 2,
    text: "Once per rest, when you succeed on an attack against an adversary, you can clear 3 Stress or a Hit Point. On a success with Hope, you also clear 3 Stress or a Hit Point on an ally within Close range of you.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, when you succeed on an attack against an adversary, you can clear 3 Stress or a Hit Point. On a success with Hope, you also clear 3 Stress or a Hit Point on an ally within Close range of you.</p>
      </>
    ),
  },
  {
    name: "Voice of Reason",
    level: 3,
    domain: "Splendor",
    type: "Ability",
    recallCost: 1,
    text: "You speak with an unmatched power and authority. You have advantage on action rolls to de-escalate violent situations or convince someone to follow your lead.\n\nAdditionally, you're emboldened in moments of duress. When all of your Stress slots are marked, you gain a +1 bonus to your Proficiency for damage rolls.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You speak with an unmatched power and authority. You have advantage on action rolls to de-escalate violent situations or convince someone to follow your lead.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Additionally, you're emboldened in moments of duress. When all of your Stress slots are marked, you gain a +1 bonus to your Proficiency for damage rolls.</p>
      </>
    ),
  },
  {
    name: "Divination",
    level: 4,
    domain: "Splendor",
    type: "Spell",
    recallCost: 1,
    text: "Once per long rest, **spend 3 Hope** to reach out to the forces beyond and ask one \"yes or no\" question about an event, person, place, or situation in the near future. For a moment, the present falls away and you see the answer before you.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, <strong className="inline-strong">spend 3 {HOPE}</strong> to reach out to the forces beyond and ask one "yes or no" question about an event, person, place, or situation in the near future. For a moment, the present falls away and you see the answer before you.</p>
      </>
    ),
  },
  {
    name: "Life Ward",
    level: 4,
    domain: "Splendor",
    type: "Spell",
    recallCost: 1,
    text: "**Spend 3 Hope** and choose an ally within Close range. They are marked with a glowing sigil of protection. When this ally would make a death move, they clear a Hit Point instead.\n\nThis effect ends when it saves the target from a death move, you cast Life Ward on another target, or you take a long rest.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend 3 {HOPE}</strong> and choose an ally within {CLOSE} range. They are marked with a glowing sigil of protection. When this ally would make a death move, they clear a {HIT_POINT} instead.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">This effect ends when it saves the target from a death move, you cast Life Ward on another target, or you take a {LONG_REST}.</p>
      </>
    ),
  },
  {
    name: "Shape Material",
    level: 5,
    domain: "Splendor",
    type: "Spell",
    recallCost: 1,
    text: "**Spend a Hope** to shape a section of natural material you're touching (such as stone, ice, or wood) to suit your purpose. The area of the material can be no larger than you. For example, you can form a rudimentary tool or create a door.\n\nYou can only affect the material within Close range of where you're touching it.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend a {HOPE}</strong> to shape a section of natural material you're touching (such as stone, ice, or wood) to suit your purpose. The area of the material can be no larger than you. For example, you can form a rudimentary tool or create a door.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">You can only affect the material within {CLOSE} range of where you're touching it.</p>
      </>
    ),
  },
  {
    name: "Smite",
    level: 5,
    domain: "Splendor",
    type: "Spell",
    recallCost: 2,
    text: "Once per rest, **spend 3 Hope** to charge your powerful smite. When you next successfully attack with a weapon, double the result of your damage roll. This attack deals magic damage regardless of the weapon's damage type.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, <strong className="inline-strong">spend 3 {HOPE}</strong> to charge your powerful smite. When you next successfully attack with a weapon, double the result of your damage roll. This attack deals magic damage regardless of the weapon's damage type.</p>
      </>
    ),
  },
  {
    name: "Restoration",
    level: 6,
    domain: "Splendor",
    type: "Spell",
    recallCost: 2,
    text: "After a long rest, place a number of tokens equal to your Spellcast trait on this card. Touch a creature and spend any number of tokens to clear 2 Hit Points or 2 Stress for each token spent.\n\nYou can also spend a token from this card when touching a creature to clear the Vulnerable condition or heal a physical or magical ailment (the GM might require additional tokens depending on the strength of the ailment).\n\nWhen you take a long rest, clear all unspent tokens.",
    rendered: (
      <>
        <p key={0} className="card-text-para">After a {LONG_REST}, place a number of tokens equal to your Spellcast trait on this card. Touch a creature and spend any number of tokens to clear 2 {HIT_POINTS} or 2 {STRESS} for each token spent.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">You can also spend a token from this card when touching a creature to clear the {VULNERABLE} condition or heal a physical or magical ailment (the GM might require additional tokens depending on the strength of the ailment).</p>
        <div key={3} className="card-text-gap" />
        <p key={4} className="card-text-para">When you take a {LONG_REST}, clear all unspent tokens.</p>
      </>
    ),
  },
  {
    name: "Zone of Protection",
    level: 6,
    domain: "Splendor",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll (16)**. Once per long rest on a success, choose a point within Far range and create a visible zone of protection there for all allies within Very Close range of that point. When you do, place a **d6** on this card with the 1 value facing up. When an ally in this zone takes damage, they reduce it by the die's value. You then increase the die's value by one. When the die's value would exceed 6, this effect ends.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (16). Once per {LONG_REST} on a success, choose a point within {FAR} range and create a visible zone of protection there for all allies within {VERY_CLOSE} range of that point. When you do, place a <strong className="inline-strong">d6</strong> on this card with the 1 value facing up. When an ally in this zone takes damage, they reduce it by the die's value. You then increase the die's value by one. When the die's value would exceed 6, this effect ends.</p>
      </>
    ),
  },
  {
    name: "Healing Strike",
    level: 7,
    domain: "Splendor",
    type: "Spell",
    recallCost: 1,
    text: "When you deal damage to an adversary, you can **spend 2 Hope** to clear a Hit Point on an ally within Close range.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you deal damage to an adversary, you can <strong className="inline-strong">spend 2 {HOPE}</strong> to clear a {HIT_POINT} on an ally within {CLOSE} range.</p>
      </>
    ),
  },
  {
    name: "Splendor-Touched",
    level: 7,
    domain: "Splendor",
    type: "Ability",
    recallCost: 2,
    text: "When 4 or more of the domain cards in your loadout are from the Splendor domain, gain the following benefits:\n\n- +3 bonus to your Severe damage threshold\n- Once per long rest, when incoming damage would require you to mark a number of Hit Points, you can choose to mark that much Stress or spend that much Hope instead.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When 4 or more of the domain cards in your {LOADOUT} are from the Splendor domain, gain the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>+3 bonus to your Severe damage threshold</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Once per {LONG_REST}, when incoming damage would require you to mark a number of {HIT_POINTS}, you can choose to mark that much {STRESS} or spend that much {HOPE} instead.</span></div>
      </>
    ),
  },
  {
    name: "Shield Aura",
    level: 8,
    domain: "Splendor",
    type: "Spell",
    recallCost: 2,
    text: "**Mark a Stress** to cast a protective aura on a target within Very Close range. When the target marks an Armor Slot, they reduce the severity of the attack by an additional threshold. If this spell causes a creature who would be damaged to instead mark no Hit Points, the effect ends.\n\nYou can only hold Shield Aura on one creature at a time.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Mark a {STRESS}</strong> to cast a protective aura on a target within {VERY_CLOSE} range. When the target marks an {ARMOR_SLOT}, they reduce the severity of the attack by an additional threshold. If this spell causes a creature who would be damaged to instead mark no {HIT_POINTS}, the effect ends.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">You can only hold Shield Aura on one creature at a time.</p>
      </>
    ),
  },
  {
    name: "Stunning Sunlight",
    level: 8,
    domain: "Splendor",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll** to unleash powerful rays of burning sunlight against all adversaries in front of you within Far range. On a success, **spend any number of Hope** and force that many targets you succeeded against to make a Reaction Roll (14).\n\nTargets who succeed take **3d20+3** magic damage. Targets who fail take **4d20+5** magic damage and are temporarily Stunned. While Stunned, they can't use reactions and can't take any other actions until they clear this condition.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} to unleash powerful rays of burning sunlight against all adversaries in front of you within {FAR} range. On a success, <strong className="inline-strong">spend any number of {HOPE}</strong> and force that many targets you succeeded against to make a Reaction Roll (14).</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Targets who succeed take <strong className="inline-strong">3d20+3</strong> magic damage. Targets who fail take <strong className="inline-strong">4d20+5</strong> magic damage and are temporarily <em>Stunned</em>. While <em>Stunned</em>, they can't use reactions and can't take any other actions until they clear this condition.</p>
      </>
    ),
  },
  {
    name: "Overwhelming Aura",
    level: 9,
    domain: "Splendor",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll (15)** to magically empower your aura. On a success, **spend 2 Hope** to make your Presence equal to your Spellcast trait until your next long rest.\n\nWhile this spell is active, an adversary must mark a Stress when they target you with an attack.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (15) to magically empower your aura. On a success, <strong className="inline-strong">spend 2 {HOPE}</strong> to make your Presence equal to your Spellcast trait until your next {LONG_REST}.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">While this spell is active, an adversary must mark a {STRESS} when they target you with an attack.</p>
      </>
    ),
  },
  {
    name: "Salvation Beam",
    level: 9,
    domain: "Splendor",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll (16)**. On a success, **mark any number of Stress** to target a line of allies within Far range. You can clear Hit Points on the targets equal to the number of Stress marked, divided among them however you'd like.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (16). On a success, <strong className="inline-strong">mark any number of {STRESS}</strong> to target a line of allies within {FAR} range. You can clear {HIT_POINTS} on the targets equal to the number of {STRESS} marked, divided among them however you'd like.</p>
      </>
    ),
  },
  {
    name: "Invigoration",
    level: 10,
    domain: "Splendor",
    type: "Spell",
    recallCost: 3,
    text: "When you or an ally within Close range has used a feature that has an exhaustion limit (such as once per rest or once per session), you can **spend any number of Hope** and roll that many **d6s**. If any roll a 6, the feature can be used again.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you or an ally within {CLOSE} range has used a feature that has an exhaustion limit (such as once per rest or once per session), you can <strong className="inline-strong">spend any number of {HOPE}</strong> and roll that many <strong className="inline-strong">d6s</strong>. If any roll a 6, the feature can be used again.</p>
      </>
    ),
  },
  {
    name: "Resurrection",
    level: 10,
    domain: "Splendor",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll (20)**. On a success, restore one creature who has been dead no longer than 100 years to full strength. Then roll a **d6**. On a result of 5 or lower, place this card in your vault permanently.\n\nOn a failure, you can't cast Resurrection again for a week.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (20). On a success, restore one creature who has been dead no longer than 100 years to full strength. Then roll a <strong className="inline-strong">d6</strong>. On a result of 5 or lower, place this card in your {VAULT} permanently.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">On a failure, you can't cast Resurrection again for a week.</p>
      </>
    ),
  },
];

export default splendorCards;
